/*
 Urql Client for calling into Appsync
*/
import { devtoolsExchange } from '@urql/devtools'
import { authExchange } from '@urql/exchange-auth'
import { retryExchange } from '@urql/exchange-retry'
import debug from 'debug'
// @ts-ignore - graphql-ws module type resolution issue
import { createClient as createWebsocketClient } from 'graphql-ws'
import { getStoredUserAuth, isTokenExpired } from 'src/utils/auth/auth-context'
import { refreshSession } from 'src/utils/auth/cognito'
import { createClient, fetchExchange, makeOperation, subscriptionExchange } from 'urql'

const logger = debug('urql')

function getToken() {
  const session = getStoredUserAuth()
  return session.idToken
}

async function refreshToken() {
  return refreshSession()
}

export async function subClient({
  websocketApi,
  selectedRoleName,
}: {
  websocketApi: string
  selectedRoleName?: string
}) {
  if (!websocketApi) {
    return
  }

  const subscriptionClient = createWebsocketClient({
    url: websocketApi,
    connectionParams: async () => {
      logger('subscriptionClient.connectionParams')
      try {
        const token = getToken()
        if (isTokenExpired(token)) {
          try {
            logger('subscriptionClient.connectionParams: refreshing token')
            const newSession = await refreshToken()

            const newToken = newSession?.idToken
            logger('subscriptionClient.connectionParams: returning refreshed token')
            return {
              token: newToken,
              selected_role_name: selectedRoleName,
            }
          } catch (e) {
            console.warn('Unable to refesh token. Logging out', e)
          }
          window.location.href = `${window.location.origin}/logout`
          return undefined
        }
        logger('subscriptionClient.connectionParams: returning token')
        return {
          token,
          // Send along selected role for data plane RBAC support (along with roles which are encoded in JWT via userService preTokenGeneration Lambda)
          selected_role_name: selectedRoleName,
          // Let back-end know to mark read receipt client as Vendia UI
          vendia_client: 'app',
        }
      } catch (err) {
        const e = err as Error
        console.error('Error attempting to get JWT token for socket connection', e?.message)
      }
    },
  })

  return subscriptionClient
}

export const makeUrqlFetchClient = async ({
  graphqlApi,
  graphqlApiKey,
  websocketApi,
  route,
  selectedRoleName,
}: {
  graphqlApi: string
  graphqlApiKey?: string
  websocketApi?: string
  route?: string
  selectedRoleName?: string
}) => {
  const subscriptionClient = await subClient({ websocketApi: websocketApi!, selectedRoleName })
  return createClient({
    url: graphqlApi,
    exchanges: [
      devtoolsExchange,
      authExchange(async () => {
        return {
          refreshAuth: async () => {
            logger('refreshAuth')
            try {
              const newSession = await refreshToken()
              const newToken = newSession?.idToken
              logger('refreshAuth: new token', newToken)
            } catch (e) {
              console.warn('Unable to refesh token. Logging out', e)
              window.location.href = `${window.location.origin}/logout`
            }
          },
          addAuthToOperation: (operation) => {
            const token = getToken()
            if (!token) {
              logger('addAuthToOperation: No token, returning operation')
              return operation
            }

            // fetchOptions can be a function (See Client API) but you can simplify this based on usage
            const fetchOptions =
              typeof operation.context.fetchOptions === 'function'
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {}

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  'x-api-key': graphqlApiKey!,
                  Authorization: `Bearer ${token}`,
                  'x-vendia-client': 'app',
                  'x-vendia-feature': route as string,
                  'x-vendia-role-name': selectedRoleName as string,
                },
              },
            })
          },
          willAuthError: () => {
            const token = getToken()
            if (isTokenExpired(token)) {
              logger('willAuthError: Token is expired, will trigger refresh')
              return true
            }
            return false
          },
          didAuthError: (error) => {
            logger('didAuthError: Server returned an error response', error?.response?.status)
            const status = error?.response?.status
            const hasUnauthorizedGraphQLError = error?.graphQLErrors?.some(
              (e: any) => e?.extensions?.code === 'UNAUTHORIZED',
            )
            return status === 401 || status === 403 || hasUnauthorizedGraphQLError
          },
        }
      }),
      retryExchange({
        initialDelayMs: 200,
        randomDelay: true,
        maxNumberAttempts: 10,
        maxDelayMs: 15000,
        retryIf: (error) => {
          return !!error.networkError && error?.response?.status !== 401 && error?.response?.status !== 403
        },
      }),
      fetchExchange,
      subscriptionExchange({
        forwardSubscription(request) {
          const input = { ...request, query: request.query || '' }
          return {
            subscribe: (sink: any) => {
              // @ts-ignore - Complex subscription client types
              const unsubscribe = subscriptionClient.subscribe(input, sink)
              return {
                unsubscribe,
              }
            },
          }
        },
      }),
    ],
  })
}
